"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { ArrowLeft, ArrowRight, AtSign, Camera, Check, Lock, Mail, Plus, Tag, User } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { AuthInput } from "@/components/auth/AuthInput";
import OtpInput from "@/components/ui/OtpInput";
import { SIGNUP_CODE_LENGTH, isCompleteSignupCode } from "@/lib/auth/signup-code";
import { createClient } from "@/lib/supabase/client";
import { OAuthButtons } from "@/components/auth/OAuthButtons";
import { PasswordStrength } from "@/components/auth/PasswordStrength";
import { PhoneField, buildFullPhone, validatePhone, EMPTY_PHONE, type PhoneValue } from "@/components/auth/PhoneField";
import { reportRejectedPhone } from "@/lib/phone/report-rejection";
import { evaluatePassword, PASSWORD_MIN_LENGTH } from "@/lib/auth/password-rules";
import { pwnedBlocksSubmit, type PwnedStatus } from "@/lib/auth/pwned-password";
import { CURRENT_TERMS_VERSION, PRIVACY_URL, TERMS_URL } from "@/lib/legal/terms";
import SignupNativeGuard from "@/app/signup/SignupNativeGuard";
import { TradeSelect } from "@/components/auth/TradeSelect";
import { isEligibleTrade, tradeLabel } from "@/lib/signup/trade-gate";
import { track } from "@/lib/analytics/track";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import MetaPixel, { trackSignupComplete } from "@/components/analytics/MetaPixel";
import SignupCookieConsent from "@/components/signup/SignupCookieConsent";
import { readMarketingConsent } from "@/lib/consent/marketing-consent";
import { bandValuePence, type SignupTeamBand } from "@/lib/meta/bands";

// The one thing someone the trade gate turned away can actually do today. Not
// in an env var: it's a public profile URL, the same in every environment.
const INSTAGRAM_URL = "https://www.instagram.com/alrightmucka";

function validateEmail(email: string): string | null {
  if (!email) return "Enter your email.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return "That doesn't look like a valid email.";
  return null;
}

// Share codes are lower/upper alphanumerics plus hyphens, capped at 30 chars.
// Used to decide whether a code is worth resolving / sending — the backend is
// the real validator, this just avoids firing the resolve call on obvious junk.
function looksLikeCode(code: string): boolean {
  return code.length > 0 && code.length <= 30 && /^[a-z0-9-]+$/i.test(code);
}

// A re-activation email links a solo lead to /signup?lead=<token>. The token
// is base64url(`email:hmac`) — the signature is verified server-side, but the
// client can safely decode just the email to pre-fill the field. Returns null
// for a missing/malformed token so an ordinary signup is unaffected.
function emailFromLeadToken(token: string | null): string | null {
  if (!token) return null;
  try {
    let b64 = token.replace(/-/g, "+").replace(/_/g, "/");
    b64 += "=".repeat((4 - (b64.length % 4)) % 4);
    const decoded = typeof atob === "function" ? atob(b64) : "";
    const idx = decoded.lastIndexOf(":");
    if (idx < 0) return null;
    const email = decoded.slice(0, idx);
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) ? email : null;
  } catch {
    return null;
  }
}

// Phase 2 ad-attribution: collect the marketing-touch data present when the
// user signs up. UTM/gclid/fbclid come from the signup URL (the marketing site
// forwards them onto app.mucka.ai/signup); referrer + landing page fall back to
// the browser when not explicitly forwarded. All optional — an organic signup
// simply yields {}. The server sanitises this (whitelist + length cap).
function collectAttribution(searchParams: { get: (key: string) => string | null }): Record<string, string> {
  const attr: Record<string, string> = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"]) {
    const v = searchParams.get(key);
    if (v) attr[key] = v;
  }
  const referrer = searchParams.get("referrer") || (typeof document !== "undefined" ? document.referrer : "");
  if (referrer) attr.referrer = referrer;
  const landing = searchParams.get("landing_page") || (typeof window !== "undefined" ? window.location.href : "");
  if (landing) attr.landing_page = landing;
  return attr;
}

// Read a browser cookie by name. Used to pull the Meta Pixel's _fbc / _fbp
// cookies at signup so the server can forward them to the Conversions API for
// match quality. Returns undefined when absent (the common case until the Pixel
// is live behind consent), which the server treats as "no cookie".
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  const found = document.cookie.split("; ").find((c) => c.startsWith(prefix));
  return found ? decodeURIComponent(found.slice(prefix.length)) : undefined;
}

// The signup team-size step. Finer than onboarding's solo/2-10/10-20/20+ so it
// can isolate the 5–30-person ICP (see src/lib/meta/bands.ts). Value = the raw
// band stored on accounts.signup_team_size; label = what the owner taps.
const TEAM_SIZE_OPTIONS: { value: SignupTeamBand; label: string }[] = [
  { value: "solo", label: "Just me" },
  { value: "2-5", label: "2–5" },
  { value: "6-10", label: "6–10" },
  { value: "11-20", label: "11–20" },
  { value: "21+", label: "21+" },
];

/**
 * The signup screens, in order.
 *   details → trade → teamSize → password → confirm   (an eligible trade)
 *   details → trade → waitlist                        (everything else)
 *
 * The trade step is a hard gate on getting an account at all, and it sits
 * SECOND on purpose. Asking last would mean walking someone through a password,
 * five strength rules, a breach check and a Terms tick before telling them we
 * don't serve their trade — a rejection we made them work for. Asking after the
 * details step means we still have the name, email and phone the waiting list
 * needs, and they've spent three fields rather than eight.
 *
 * The teamSize step follows the gate: it's the ICP signal (5–30-person firm vs
 * solo trader) that feeds the Meta conversion value, so we only ask it of a
 * trade we serve, and before the password so it's captured even if they drop at
 * the password step.
 */
type Step = "details" | "trade" | "teamSize" | "password" | "confirm" | "waitlist";

function SignupFlow({ initialReferralCode }: { initialReferralCode?: string }) {
  const searchParams = useSearchParams();
  const next = searchParams.get("next");
  // Solo-lead re-activation deep link: carries the lead through to the signup
  // POST for conversion attribution; the email is pre-filled from it.
  const leadToken = searchParams.get("lead");

  // Named rather than numbered. The trade gate added two screens in the middle
  // and at the end, and every `step !== 2` in here would have silently come to
  // mean something else.
  const [step, setStep] = useState<Step>("details");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(() => emailFromLeadToken(searchParams.get("lead")) ?? "");
  // { country, local } rather than a bare string — the country code is a
  // picker now, and the two halves have to travel together or buildFullPhone
  // silently reassembles the wrong number.
  const [phone, setPhone] = useState<PhoneValue>(EMPTY_PHONE);

  /**
   * Every trade they picked, main first. A LIST since 11 September 2026: the
   * workspace has held one since the settings picker landed, and signup was the
   * last place insisting on a single answer — so a gas engineer who also does
   * plumbing and bathrooms started with one third of his job types and had to go
   * and find Settings to fix it.
   *
   * The FIRST entry is the main trade and goes to `accounts.trade`, which a
   * dozen unrelated readers key off. The whole list goes to `accounts.trades`,
   * and every family in it gets its presets seeded.
   */
  const [trades, setTrades] = useState<string[]>([]);
  // The main trade, for everything that still wants exactly one: the waiting
  // list, the blocked-trade copy, the analytics property.
  const mainTrade = trades[0] ?? "";
  // ICP band chosen on the teamSize step — "" until picked. Drives the Meta
  // conversion value sent at signup.
  const [teamSize, setTeamSize] = useState<SignupTeamBand | "">("");

  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwnedStatus, setPwnedStatus] = useState<PwnedStatus>("idle");
  // Explicit Terms of Use / Privacy Policy acceptance. Required (a real tick,
  // not implied by use) and recorded server-side against the new user so we can
  // prove who agreed to which version and when. See src/lib/legal/terms.ts.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Referral: the vanity route (/signup/<code>) passes initialReferralCode as a
  // prop, which takes precedence over ?ref=. When neither is present the code
  // can still be typed manually via the "Got a referral code?" toggle. The
  // resolve call is debounced and purely informational — an invalid or
  // unresolved code never blocks signup; the backend re-validates on submit.
  const codeFromUrl = Boolean(initialReferralCode || searchParams.get("ref"));
  const [referralCode, setReferralCode] = useState(
    () => (initialReferralCode || searchParams.get("ref") || "").trim(),
  );
  const [showRefInput, setShowRefInput] = useState(false);
  const [refResolve, setRefResolve] = useState<
    { valid: boolean; discountPct?: number; referrerFirstName?: string } | null
  >(null);
  const [refChecking, setRefChecking] = useState(false);

  // Discounts are web-only. The referral entry, its discount confirmation, the
  // resolve call, and the code we send are all suppressed inside the native
  // apps: a referral discount can only ever be realised via the web Stripe
  // checkout (IAP pricing can't honour a Stripe coupon), so surfacing it in the
  // binary is both wrong and an App Store 3.1.1 / 3.1.3 risk. Default true
  // (assume native until mounted) so it never flashes in the app; flips to
  // false on web after mount, SSR-safe.
  const [native, setNative] = useState(true);
  useEffect(() => {
    setNative(Capacitor.isNativePlatform());
  }, []);

  // The cookie question — web only (the native app runs no ad pixel), and only
  // for somebody who has not answered on this page or on mucka.ai.
  const [cookieChoiceOpen, setCookieChoiceOpen] = useState(false);
  useEffect(() => {
    if (!native && readMarketingConsent() === null) setCookieChoiceOpen(true);
  }, [native]);

  useEffect(() => {
    const code = referralCode.trim();
    // No referral resolution inside the app — discounts are web-only.
    if (native || !looksLikeCode(code)) {
      setRefResolve(null);
      setRefChecking(false);
      return;
    }
    setRefChecking(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/referrals/resolve?code=${encodeURIComponent(code)}`);
        // Only a clean 200 is trusted. A server hiccup shouldn't tell the user
        // their code "wasn't recognised" — leave the strip empty in that case.
        if (res.ok) {
          const data = await res.json();
          setRefResolve(data);
        } else {
          setRefResolve(null);
        }
      } catch {
        setRefResolve(null);
      } finally {
        setRefChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [referralCode, native]);

  // Only send a code that looks structurally valid, and never from inside the
  // app (form or /signup/<code> deep link) — that keeps a native signup from
  // snapshotting a discount. The backend re-checks `native` as belt-and-braces.
  const referralCodeToSend =
    !native && looksLikeCode(referralCode.trim()) ? referralCode.trim() : undefined;
  const discountPct = refResolve?.discountPct ?? 10;

  // Only iOS native gets the "Open mailbox" affordance — `message://`
  // launches Mail.app straight to the inbox. Android has no equivalent
  // URL scheme (would need a custom Intent plugin), and web users will
  // open their email themselves. Set after mount so SSR matches client.
  const [showMailboxBtn, setShowMailboxBtn] = useState(false);
  useEffect(() => {
    setShowMailboxBtn(
      Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios",
    );
  }, []);

  // Resend, on the confirmation screen. An unconfirmed address is the state
  // that used to spawn duplicate accounts — someone who never got the first
  // email came back and signed up again, and the API happily built them a
  // second workspace. The API no longer does that (it detects the existing
  // auth user), which makes a resend button the only thing they actually
  // needed in the first place.
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  async function handleResend() {
    setResending(true);
    const supabase = createClient();
    // The outcome is deliberately NOT surfaced. This screen is shown to a new
    // signup, to a returning unconfirmed one, and (via the API's obfuscated
    // branch) to someone whose account is already confirmed — so an error like
    // "email already confirmed" would leak which addresses are registered, on
    // the one screen where that leak is free to harvest. Supabase's resend
    // endpoint is anti-enumeration-safe for the same reason.
    await supabase.auth
      .resend({
        type: "signup",
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?type=signup`,
        },
      })
      .catch(() => { /* neutral either way — see above */ });
    setResent(true);
    setResending(false);
  }

  /*
   * VERIFY BY CODE.
   *
   * Two of the last seven signups on 14 September 2026 never confirmed their
   * email and never got into Mucka at all: no sign-in, no onboarding_progress
   * row, nothing. Nobody reports a thing that did not happen.
   *
   * 🚨 The cross-device argument does NOT apply here and an earlier draft of
   * this comment claimed it did. Mucka's Confirm-signup template links to
   * `/auth/callback?token_hash=…&type=signup`, which is redeemed with
   * `verifyOtp` and works from any device. PKCE only binds the `code` branch,
   * and this template has never sent one. Getting that wrong in a comment is
   * how the next person ships a fix for a problem we do not have.
   *
   * What a link genuinely costs: it throws the owner out of the app into
   * whatever browser their mail client prefers, it is the one thing corporate
   * mail rewrites (Outlook SafeLinks burns the token in transit — already
   * documented for password resets in the help article), and it cannot be
   * offered off a phone keyboard. A code is typed where they already are.
   *
   * 🚨 `type: 'signup'` — the same otpType `/auth/callback` maps `?type=signup`
   * to, because it is the same token from the same Supabase template, only
   * delivered as digits instead of inside a URL. How MANY digits is a
   * dashboard setting; see `SIGNUP_CODE_LENGTH`, and never hard-code it.
   *
   * 🚨 Verified through the BROWSER client on purpose. `verifyOtp` mints the
   * session, and `createBrowserClient` is what writes the cookies the
   * middleware then reads. Calling this from a plain `supabase-js` client would
   * verify the address and leave them signed out on the screen that just told
   * them they were in.
   */
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  async function handleVerify(fullCode: string) {
    if (!isCompleteSignupCode(fullCode) || verifying) return;
    setVerifying(true);
    setCodeError(null);
    const supabase = createClient();
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: fullCode,
      type: "signup",
    });
    if (error) {
      // The server's own words, because it is the only participant that knows
      // whether this was the wrong code or an expired one, and "that didn't
      // work" sends somebody to re-check digits they typed correctly
      // (anti-pattern #35). The code is left in the boxes — clearing it turns a
      // one-digit correction into typing it all again from memory.
      const msg = error.message.toLowerCase();
      setCodeError(
        msg.includes("expired")
          ? "That code has expired. Send a new one and try again."
          : msg.includes("invalid")
            ? "That code isn't right. Give it another go."
            : error.message,
      );
      setVerifying(false);
      return;
    }
    // 🚨 A hard navigation, not router.push. The session cookies were just
    // written by the browser client and the middleware reads them server-side;
    // a client transition would route off a tree rendered before they existed.
    window.location.assign("/");
  }

  // Password-step friction telemetry. Refs (not state) so counting a breach hit
  // or marking "requirements met" never triggers a re-render — these only feed
  // Amplitude, they don't drive UI. See ANALYTICS_EVENTS SIGNUP_PASSWORD_* notes.
  const reqsMetFired = useRef(false);
  const breachHits = useRef(0);

  // All five checks green (4 composition rules + breach clear; `error` = HIBP
  // unreachable, which we fail open on, same as the checklist tick).
  const passwordFullyValid =
    evaluatePassword(password).allMet &&
    (pwnedStatus === "safe" || pwnedStatus === "error");

  // Fire once, the first time the password becomes fully valid on the password
  // step — the "got past the rules" milestone the funnel needs.
  useEffect(() => {
    if (step === "password" && passwordFullyValid && !reqsMetFired.current) {
      reqsMetFired.current = true;
      track(ANALYTICS_EVENTS.SIGNUP_PASSWORD_REQUIREMENTS_MET, {
        hit_breach_warning: breachHits.current > 0,
        breach_hits: breachHits.current,
      });
    }
  }, [step, passwordFullyValid]);

  // Each time the live breach check rejects their chosen password.
  useEffect(() => {
    if (pwnedStatus === "pwned") {
      breachHits.current += 1;
      track(ANALYTICS_EVENTS.SIGNUP_PASSWORD_BREACHED, { attempt: breachHits.current });
    }
  }, [pwnedStatus]);

  function handleDetailsSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    const next: Record<string, string> = {};
    if (!firstName.trim()) next.firstName = "Enter your first name.";
    if (!lastName.trim()) next.lastName = "Enter your surname.";
    const emailErr = validateEmail(email.trim());
    if (emailErr) next.email = emailErr;
    const phoneErr = validatePhone(phone);
    if (phoneErr) {
      next.phone = phoneErr;
      reportRejectedPhone(phone, phoneErr, "signup");
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    // Top of the activation funnel — details validated, moving to the trade
    // gate. No PII in props (email/name are never sent to analytics). A referral
    // code is a share token, not PII, so it rides along when present.
    track(ANALYTICS_EVENTS.SIGNUP_STARTED, referralCodeToSend ? { referral_code: referralCodeToSend } : undefined);
    setStep("trade");
  }

  // The trade step is the gate, and it runs BEFORE the password step: making
  // someone pick a password, meet five rules and accept the Terms, and only
  // then telling them we don't serve their trade, is a rejection we made them
  // work for. It also means the waiting list costs them three fields.
  function handleTradeSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (trades.length === 0) {
      setErrors({ trade: "Choose your trade." });
      return;
    }
    setErrors({});
    /*
     * ANY eligible trade admits them, and the server applies the same rule.
     * The gate asks whether Mucka is for this business, and a firm that does
     * gas AND electrical work is a heating firm with a sideline — turning it
     * away because the second tick was an electrician would be the gate
     * answering a question nobody asked. A list with nothing eligible in it is
     * still a waiting-list entry, filed under the main trade.
     */
    if (!trades.some(isEligibleTrade)) {
      setLoading(true);
      void handleBlockedTrade().finally(() => setLoading(false));
      return;
    }
    setStep("teamSize");
  }

  // How big is the firm? The one ICP signal we can capture at the top of the
  // funnel — a solo trader and a 5–30-person firm are worth very different
  // amounts, and this is what tells Meta which it just got. Required, one tap.
  function handleTeamSizeSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (!teamSize) {
      setErrors({ teamSize: "Pick your team size." });
      return;
    }
    setErrors({});
    setStep("password");
  }

  // A trade we don't serve: no account, no auth user, no confirmation email —
  // just their details on the waiting list and the "coming soon" screen. The
  // server enforces the same rule (POST /api/auth/signup 403s on an ineligible
  // trade); this branch is what makes the experience right, not what makes it
  // safe.
  //
  // `termsAccepted` is normally FALSE here, and that is correct rather than an
  // oversight: the tick lives on the password step, which a blocked trade never
  // reaches. The Terms of Use govern having an account, and they aren't getting
  // one. What justifies the row is the promise made on the screen they land on
  // — collected in context, for one stated purpose. (It IS true on the rare
  // path in from the password step below, when the server's gate disagrees with
  // ours, so the flag is still worth sending.)
  async function handleBlockedTrade(): Promise<void> {
    track(ANALYTICS_EVENTS.SIGNUP_TRADE_BLOCKED, { trade: mainTrade });
    try {
      await fetch("/api/signup/waiting-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: buildFullPhone(phone),
          // The waiting list is one row per person, so it carries the main
          // trade — the one the "coming soon" screen names back to them.
          trade: mainTrade,
          attribution: collectAttribution(searchParams),
          referralCode: referralCodeToSend,
          termsAccepted: acceptedTerms,
        }),
      });
    } catch {
      // Deliberately swallowed. There is nothing this person can do about a
      // failed write, and sending them back to a form to re-enter details we
      // are not going to act on today would be worse than the lost row. The
      // route reports its own failures to Sentry.
    }
    setStep("waitlist");
  }

  // Last step, and the one that creates the account. The trade was cleared two
  // screens ago, so by the time anyone types a password we already know we can
  // serve them.
  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    const next: Record<string, string> = {};
    if (!evaluatePassword(password).allMet) next.password = "Meet all the rules below.";
    if (confirmPw !== password) next.confirmPw = "Passwords don't match.";
    if (!acceptedTerms) next.terms = "Please accept the Terms of Use and Privacy Policy to continue.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    // One id shared by the browser Pixel fire and the server Conversions API
    // event, so Meta dedupes the redundant pair. Generated here so both sides
    // agree; the server falls back to its own if an older bundle omits it.
    const capiEventId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `sg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: buildFullPhone(phone),
          password,
          // Both, deliberately. `trade` is the main one and stays the field a
          // dozen readers key off; `trades` is the whole answer. An older
          // bundle sends only `trade` and the route still works.
          trade: mainTrade,
          trades,
          teamSize: teamSize || undefined,
          attribution: collectAttribution(searchParams),
          leadToken: leadToken || undefined,
          referralCode: referralCodeToSend,
          native,
          termsAccepted: acceptedTerms,
          termsVersion: CURRENT_TERMS_VERSION,
          // Meta conversion dedup + match: the shared event id and the Pixel's
          // click cookies (present only once the Pixel is live behind consent).
          eventId: capiEventId,
          fbc: readCookie("_fbc"),
          fbp: readCookie("_fbp"),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // The server's own gate disagreeing with ours — a stale bundle after a
        // change to the eligible categories. Honour the server and route them
        // to the waiting list rather than showing an error they can't act on.
        // Rare now that the picker runs two screens earlier, but it is the only
        // thing standing between a stale client and a confusing dead end.
        if (res.status === 403 && data.code === "trade_not_supported") {
          await handleBlockedTrade();
          setLoading(false);
          return;
        }
        setServerError(data.error || "Something went wrong.");
        setLoading(false);
        return;
      }
      // Account created — the activation funnel's key conversion. Carry the
      // password-friction context so completions can be segmented by whether the
      // breach guard got in the way (high breach_hits among completers = tolerated
      // friction; the same among drop-offs = friction that cost you the signup).
      track(ANALYTICS_EVENTS.SIGNUP_COMPLETED, {
        hit_breach_warning: breachHits.current > 0,
        breach_hits: breachHits.current,
        trade: mainTrade,
        trade_count: trades.length,
        ...(referralCodeToSend ? { referral_code: referralCodeToSend } : {}),
      });
      // Browser-side Meta conversion, valued by ICP band, sharing capiEventId
      // with the server CAPI event for dedup. No-ops until the Pixel is live
      // behind consent; the server event fires regardless.
      if (teamSize) trackSignupComplete(capiEventId, bandValuePence(teamSize));
      setStep("confirm");
      setLoading(false);
    } catch {
      setServerError("Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/mucka-orange-logo.png" alt="Mucka" className="mucka-auth-logo" />

      {step === "details" && (
        <>
          <h1 className="t-display-lg mucka-auth-title">Alright, Mucka!</h1>
          <p className="t-body-md mucka-auth-sub">Let&apos;s get you set up.</p>
          <p className="t-body-md mucka-auth-prompt">What should I call you?</p>

          {/* Referral confirmation strip — web only, and only when a code resolves valid. */}
          {!native && referralCode.trim() && refResolve?.valid && (
            <div className="mucka-auth-banner mucka-auth-banner--info ref-signup-note">
              <strong>{discountPct}% off for 12 months applied</strong>
              {refResolve.referrerFirstName ? (
                <span className="ref-signup-note-thanks">Thanks to {refResolve.referrerFirstName}.</span>
              ) : null}
            </div>
          )}
          {/* Unrecognised code — informational only, signup still goes ahead. */}
          {!native && referralCode.trim() && refResolve && !refResolve.valid && !refChecking && (
            <p className="ref-signup-hint">That code wasn&apos;t recognised, but you can still sign up.</p>
          )}

          <form className="mucka-auth-form" onSubmit={handleDetailsSubmit} noValidate>
            <AuthInput
              icon={User}
              autoComplete="given-name"
              placeholder="Enter first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={errors.firstName}
            />
            <AuthInput
              icon={User}
              autoComplete="family-name"
              placeholder="Enter surname"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={errors.lastName}
            />
            <AuthInput
              icon={AtSign}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
            />
            <PhoneField
              value={phone}
              onChange={setPhone}
              error={errors.phone}
            />

            {/* Manual code entry — web only (discounts are web-only), and only
                when no code arrived via URL. */}
            {!native && !codeFromUrl && (
              <div className="ref-signup-toggle">
                {!showRefInput ? (
                  <button
                    type="button"
                    className="ref-signup-link"
                    onClick={() => setShowRefInput(true)}
                  >
                    Got a referral code?
                  </button>
                ) : (
                  <AuthInput
                    icon={Tag}
                    autoComplete="off"
                    placeholder="Enter referral code"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.trim())}
                  />
                )}
              </div>
            )}

            <div className="mucka-auth-cta">
              <Button type="submit" variant="primary" size="lg" fullWidth leadingIcon={Plus}>
                Create Account
              </Button>

              {process.env.NEXT_PUBLIC_ENABLE_OAUTH === "true" && (
                <>
                  <div className="mucka-auth-divider">or</div>
                  <OAuthButtons next={next} intent="signup" />
                </>
              )}
            </div>
          </form>
        </>
      )}

      {step === "password" && (
        <>
          <h1 className="t-display-lg mucka-auth-title">
            Nice to meet you, {firstName.split(" ")[0] || "Mucka"}!
          </h1>
          <p className="t-body-md mucka-auth-sub" style={{ marginBottom: 28 }}>
            Now let&apos;s secure your account.
          </p>

          {serverError && (
            <div className="mucka-auth-banner mucka-auth-banner--error">
              {serverError}
            </div>
          )}

          <form className="mucka-auth-form" onSubmit={handlePasswordSubmit} noValidate>
            <AuthInput
              icon={Lock}
              type="password"
              passwordToggle
              autoComplete="new-password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setServerError(null); }}
              error={errors.password}
              minLength={PASSWORD_MIN_LENGTH}
              aria-describedby="signup-pw-checks"
            />
            <AuthInput
              icon={Lock}
              type="password"
              passwordToggle
              autoComplete="new-password"
              placeholder="Re-enter password"
              value={confirmPw}
              onChange={(e) => { setConfirmPw(e.target.value); setServerError(null); }}
              error={errors.confirmPw}
            />
            <PasswordStrength
              password={password}
              confirmPassword={confirmPw}
              id="signup-pw-checks"
              onPwnedStatusChange={setPwnedStatus}
            />

            <div className={`mucka-auth-terms${errors.terms ? " is-error" : ""}`}>
              <button
                type="button"
                role="checkbox"
                aria-checked={acceptedTerms}
                aria-describedby={errors.terms ? "signup-terms-error" : undefined}
                className={`mucka-auth-terms-box${acceptedTerms ? " is-checked" : ""}`}
                onClick={() => {
                  setAcceptedTerms((v) => !v);
                  if (errors.terms) {
                    setErrors((prev) => {
                      const { terms, ...rest } = prev;
                      void terms;
                      return rest;
                    });
                  }
                }}
              >
                {acceptedTerms && <Check size={14} strokeWidth={3} />}
              </button>
              <span className="mucka-auth-terms-text">
                I agree to the{" "}
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </a>{" "}
                and{" "}
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </span>
            </div>
            {errors.terms && (
              <p id="signup-terms-error" className="mucka-auth-terms-err">
                {errors.terms}
              </p>
            )}

            <div className="mucka-auth-cta mucka-auth-cta--row">
              <button
                type="button"
                className="mucka-auth-back"
                aria-label="Back"
                onClick={() => {
                  setErrors({});
                  setStep("teamSize");
                }}
              >
                <Icon icon={ArrowLeft} size={18} />
              </button>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                leadingIcon={Plus}
                disabled={loading || !evaluatePassword(password).allMet || confirmPw !== password || pwnedBlocksSubmit(pwnedStatus) || !acceptedTerms}
              >
                {loading ? "Creating account…" : "Create Account"}
              </Button>
            </div>
          </form>
        </>
      )}

      {step === "trade" && (
        <>
          <h1 className="t-display-lg mucka-auth-title">What&apos;s your trade?</h1>
          <p className="t-body-md mucka-auth-sub" style={{ marginBottom: 28 }}>
            Pick everything you actually do. Mucka turns up knowing the job types,
            quotes and paperwork for each one.
          </p>

          {serverError && (
            <div className="mucka-auth-banner mucka-auth-banner--error">{serverError}</div>
          )}

          <form className="mucka-auth-form" onSubmit={handleTradeSubmit} noValidate>
            <TradeSelect
              id="signup-trade"
              value={trades}
              onChange={(v) => {
                setTrades(v);
                setErrors({});
                setServerError(null);
              }}
              error={errors.trade}
            />

            <div className="mucka-auth-cta mucka-auth-cta--row">
              <button
                type="button"
                className="mucka-auth-back"
                aria-label="Back"
                onClick={() => {
                  setErrors({});
                  setServerError(null);
                  setStep("details");
                }}
              >
                <Icon icon={ArrowLeft} size={18} />
              </button>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                trailingIcon={ArrowRight}
                disabled={loading || trades.length === 0}
              >
                Continue
              </Button>
            </div>
          </form>
        </>
      )}

      {step === "teamSize" && (
        <>
          <h1 className="t-display-lg mucka-auth-title">How big&apos;s your team?</h1>
          <p className="t-body-md mucka-auth-sub" style={{ marginBottom: 28 }}>
            Helps Mucka fit how you work, whether it&apos;s just you or a full crew.
          </p>

          {serverError && (
            <div className="mucka-auth-banner mucka-auth-banner--error">{serverError}</div>
          )}

          <form className="mucka-auth-form" onSubmit={handleTeamSizeSubmit} noValidate>
            {/* Not <Button variant="tertiary">: its chamfered outline is the
                app's language, and against signup's soft ghost-grey fields a
                stack of outlined boxes read as pasted in from another screen.
                .mucka-auth-choice matches the inputs above it instead. */}
            <div className="mucka-auth-choices" role="radiogroup" aria-label="Team size">
              {TEAM_SIZE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={teamSize === opt.value}
                  className="mucka-auth-choice"
                  onClick={() => {
                    setTeamSize(opt.value);
                    if (errors.teamSize) setErrors({});
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {errors.teamSize && <p className="mucka-auth-terms-err">{errors.teamSize}</p>}

            <div className="mucka-auth-cta mucka-auth-cta--row">
              <button
                type="button"
                className="mucka-auth-back"
                aria-label="Back"
                onClick={() => {
                  setErrors({});
                  setServerError(null);
                  setStep("trade");
                }}
              >
                <Icon icon={ArrowLeft} size={18} />
              </button>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                trailingIcon={ArrowRight}
                disabled={loading || !teamSize}
              >
                Continue
              </Button>
            </div>
          </form>
        </>
      )}

      {step === "waitlist" && (
        <div className="mucka-auth-waitlist">
          <h1 className="t-display-lg mucka-auth-title">
            {tradeLabel(mainTrade) ?? "Your trade"}, coming soon.
          </h1>

          <div className="mucka-auth-waitlist-body">
            <p className="t-body-md mucka-auth-sub">
              Right now Mucka is built round heating, plumbing and air conditioning firms.
            </p>
            <p className="t-body-md mucka-auth-sub">
              You&apos;re on the list. The day we open up to{" "}
              <strong>{tradeLabel(mainTrade) ?? "your trade"}</strong>, we&apos;ll email{" "}
              <strong>{email}</strong>.
            </p>
          </div>

          <div className="mucka-auth-cta">
            {/* Chip + auto arrow, so it carries the same weight as the Create
                Account button the page before it. `Camera` in the chip because
                lucide dropped its brand icons and there is no Instagram glyph
                to reach for — swap in a real mark via `chipImageSrc` if we ever
                add one to /public/brand. */}
            <Button
              variant="primary"
              size="lg"
              fullWidth
              leadingIcon={Camera}
              onClick={() => window.open(INSTAGRAM_URL, "_blank", "noopener,noreferrer")}
            >
              Stay up to date
            </Button>
          </div>

          {/* Demoted from a button: they've been turned away, and the loudest
              thing on the screen shouldn't be "you got it wrong". Same shape as
              the "Already have an account? Sign in" line — question, then verb. */}
          <p className="mucka-auth-foot">
            Picked the wrong trade?
            <button
              type="button"
              onClick={() => {
                setErrors({});
                setServerError(null);
                setStep("trade");
              }}
            >
              Change it
            </button>
          </p>
        </div>
      )}

      {step === "confirm" && (
        <>
          <h1 className="t-display-lg mucka-auth-title">You&apos;re in!</h1>
          <p className="t-body-md mucka-auth-sub">
            We&apos;ve sent a code to <strong>{email}</strong>. Pop it in below and
            you&apos;re away.
          </p>

          {/* Autofocused, because this screen has exactly one job and making
              somebody hunt for the first box is the whole friction we are
              here to remove. iOS offers the code straight off the keyboard
              bar via autoComplete="one-time-code". */}
          <div className="mucka-auth-cta" style={{ marginTop: 18 }}>
            <OtpInput
              value={code}
              onChange={(v) => {
                setCode(v);
                if (codeError) setCodeError(null);
              }}
              onComplete={handleVerify}
              length={SIGNUP_CODE_LENGTH}
              disabled={verifying}
              label="Verification code"
            />
            {codeError && (
              <p className="mucka-onb-error" role="alert" style={{ marginTop: 10 }}>
                {codeError}
              </p>
            )}
            {verifying && (
              <p className="t-body-sm mucka-auth-sub" role="status" style={{ marginTop: 10 }}>
                Checking&hellip;
              </p>
            )}
          </div>
          <p className="mucka-auth-sub" style={{ marginTop: 10, fontSize: 13, color: 'var(--text-dim)' }}>
            Can&apos;t find it? Have a quick look in your junk or spam folder.
            {resent ? (
              <> We&apos;ve sent a new code to <strong>{email}</strong>.</>
            ) : (
              <>
                {" "}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="mucka-auth-banner-link"
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  {resending ? "Sending…" : "Send it again"}
                </button>
              </>
            )}
          </p>

          {showMailboxBtn && (
            <div className="mucka-auth-cta">
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                leadingIcon={Mail}
                onClick={() => {
                  window.location.href = "message://";
                }}
              >
                Open mailbox
              </Button>
            </div>
          )}
        </>
      )}

      {step !== "confirm" && step !== "waitlist" && (
        <p className="mucka-auth-foot">
          Already have an account?
          <a href={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`}>Sign in</a>
        </p>
      )}

      {/* The password step carries an explicit required tick-box, so the passive
          footer line would be redundant/contradictory there — show it only on
          the other steps (name entry, trade, confirmation) for continuity. */}
      {step !== "password" && (
        <p className="mucka-auth-legal">
          By using this app, you agree to our{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
            Terms of Use
          </a>
          .
          {!native && (
            <>
              {" "}
              <button type="button" className="mucka-auth-legal-link" onClick={() => setCookieChoiceOpen(true)}>
                Cookie settings
              </button>
            </>
          )}
        </p>
      )}

      {!native && <MetaPixel />}
      <SignupCookieConsent open={!native && cookieChoiceOpen} onAnswer={() => setCookieChoiceOpen(false)} />
    </>
  );
}

/**
 * Client-side signup wizard. Rendered by both /signup (thin server wrapper) and
 * the vanity referral route /signup/<code>. `initialReferralCode` comes from
 * the vanity path segment and takes precedence over a ?ref= query param.
 */
export default function SignupClient({ initialReferralCode }: { initialReferralCode?: string }) {
  return (
    <div className="mucka-auth-page mucka-auth-page--signup">
      <div className="mucka-auth-container">
        <Suspense>
          <SignupNativeGuard />
          <SignupFlow initialReferralCode={initialReferralCode} />
        </Suspense>
      </div>
    </div>
  );
}
